import { Button } from '../../../../components/button';
import {
  Select,
  createSelectListCollection,
} from '../../../../components/select';
import { roleOptions } from '../../../application-credentials';
import { Box } from '@terra/ui-styled-system';
import { Tick01Icon, ArrowDown01Icon, Add01Icon } from '@terra/ui-icons';
import { useMemo } from 'react';
import { type CustomRoleDraft } from '../AddCredentialsDialogStore';

type RoleSelectProps = {
  customRoles: CustomRoleDraft[];
  selectedRole: string;
  placeholder: string;
  createRoleLabel: string;
  onRoleChange: (role: string) => void;
  onCreateRoleClick: () => void;
};
const CUSTOM_ROLE_PREFIX = 'custom:';

export const RoleSelect = ({
  customRoles,
  selectedRole,
  placeholder,
  createRoleLabel,
  onRoleChange,
  onCreateRoleClick,
}: RoleSelectProps) => {
  const customRoleItems = useMemo(
    () =>
      customRoles.map((role) => ({
        label: role.name,
        value: `${CUSTOM_ROLE_PREFIX}${role.name}`,
      })),
    [customRoles],
  );
  const selectedRoleValue = customRoles.some(
    (customRole) => customRole.name === selectedRole,
  )
    ? `${CUSTOM_ROLE_PREFIX}${selectedRole}`
    : selectedRole.toLowerCase() === 'other'
      ? 'OTHER'
      : selectedRole;
  const orphanRoleItems = useMemo(() => {
    if (!selectedRoleValue) {
      return [];
    }

    const existsInOptions =
      customRoleItems.some((item) => item.value === selectedRoleValue) ||
      roleOptions.items.some((item) => item.value === selectedRoleValue);

    if (existsInOptions) {
      return [];
    }

    return [{ label: selectedRole, value: selectedRoleValue }];
  }, [customRoleItems, selectedRole, selectedRoleValue]);
  const roleCollection = useMemo(
    () =>
      createSelectListCollection({
        items: [...orphanRoleItems, ...roleOptions.items, ...customRoleItems],
      }),
    [customRoleItems, orphanRoleItems],
  );

  return (
    <Select.Root
      size="sm"
      positioning={{ sameWidth: true, strategy: 'fixed' }}
      value={selectedRoleValue ? [selectedRoleValue] : []}
      collection={roleCollection}
      onValueChange={({ value }) => {
        const selectedValue = value[0];

        if (selectedValue?.startsWith(CUSTOM_ROLE_PREFIX)) {
          onRoleChange(selectedValue.replace(CUSTOM_ROLE_PREFIX, ''));

          return;
        }

        onRoleChange(selectedValue ?? '');
      }}
    >
      <Select.Control>
        <Select.Trigger>
          <Select.ValueText
            placeholder={placeholder}
            fontSize="xs"
            color={
              selectedRoleValue ? 'fg.neutral.default' : 'fg.neutral.muted'
            }
          />
          <ArrowDown01Icon size={16} />
        </Select.Trigger>
      </Select.Control>
      <Select.Positioner>
        <Select.Content maxH="320px" overflowY="auto">
          {orphanRoleItems.length > 0 && (
            <Select.ItemGroup>
              {orphanRoleItems.map((item) => (
                <Select.Item key={item.value} item={item}>
                  <Select.ItemText>{item.label}</Select.ItemText>
                  <Select.ItemIndicator>
                    <Tick01Icon size={16} />
                  </Select.ItemIndicator>
                </Select.Item>
              ))}
            </Select.ItemGroup>
          )}
          <Select.ItemGroup>
            {roleOptions.items.map((option) => (
              <Select.Item key={option.value} item={option}>
                <Select.ItemText>{option.label}</Select.ItemText>
                <Select.ItemIndicator>
                  <Tick01Icon size={16} />
                </Select.ItemIndicator>
              </Select.Item>
            ))}
          </Select.ItemGroup>
          {customRoleItems.length > 0 && (
            <>
              <Box borderTopWidth="1px" borderColor="border.default" />
              <Select.ItemGroup>
                {customRoleItems.map((item) => (
                  <Select.Item key={item.value} item={item}>
                    <Select.ItemText>{item.label}</Select.ItemText>
                    <Select.ItemIndicator>
                      <Tick01Icon size={16} />
                    </Select.ItemIndicator>
                  </Select.Item>
                ))}
              </Select.ItemGroup>
            </>
          )}
          <Box px={2} py={1} borderTopWidth="1px" borderColor="border.default">
            <Button
              variant="ghost"
              size="xs"
              w="full"
              justifyContent="flex-start"
              onClick={onCreateRoleClick}
            >
              <Add01Icon size={14} />
              {createRoleLabel}
            </Button>
          </Box>
        </Select.Content>
      </Select.Positioner>
    </Select.Root>
  );
};
